import { __ } from "@wordpress/i18n";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartLegend from "../../../../components/ChartLegend/ChartLegend";
import { utils } from "../../../../helpers";
import { useResult } from "../../../../shared/formContext/hooks/useResult";
import {SavingsCertificatesResult} from "@blocks/savings-certificates/interfaces/savingsCertificatesTypes.ts";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

import { useState, useEffect, useRef } from '@wordpress/element';

/**
 * Chart colors configuration
 */
const CHART_COLORS = {
  INVESTED_CAPITAL: "#CCEBF5",
  TOTAL_ACCUMULATED: "#0099CC",
} as const;

/**
 * TabSavingsEvolution Component
 *
 * Standalone implementation with direct Chart.js integration
 * Ensures tooltip and legend functionality work perfectly together
 *
 * @returns {JSX.Element | null} The chart component or null if no data
 */
const TabSavingsEvolution = (): JSX.Element | null => {
  const { resultData } = useResult();
  const { response } = resultData;

  const apiResult = response?.data.result as SavingsCertificatesResult;
  const schedule = apiResult.monthly_breakdown || []

  const [legends, setLegends] = useState();
  const [chartData, setChartData] = useState(null);
  const [tooltipDataIndex, setTooltipDataIndex] = useState();
  const chartRef = useRef(null);

  /**
   * Updates legend values dynamically for both lines
   */
  const updateLegend = (tooltipItem: any) => {
    const dataIndex = tooltipItem.dataIndex;
    const dataPoint = schedule[dataIndex];

    if (!dataPoint) return;

    setLegends((previousLegends) => ({
      ...previousLegends,
      [__("Capital investido", "df-simulators")]: {
        ...previousLegends[__("Capital investido", "df-simulators")],
        value: dataPoint.total_invested || 0,
      },
      [__("Investimento + juros líquidos acumulados", "df-simulators")]: {
        ...previousLegends[
          __("Investimento + juros líquidos acumulados", "df-simulators")
        ],
        value: dataPoint.net_total || 0,
      },
    }));
  };

  /**
   * Custom plugin for vertical hover line
   */
  const hoverLinePlugin = {
    beforeDraw: (chart: any) => {
      if (chart.tooltip._active && chart.tooltip._active.length) {
        const activePoint = chart.tooltip._active[0];
        const { ctx } = chart;
        const { x } = activePoint.element;
        const topY = chart.scales.y.top;
        const bottomY = chart.scales.y.bottom;

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, topY);
        ctx.lineTo(x, bottomY);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#DEE0E2";
        ctx.stroke();
        ctx.restore();
      }
    },
  };

  useEffect(() => {
    if (!Array.isArray(schedule) || schedule.length === 0) return;

    // Initialize legend
    setLegends({
      [__("Capital investido", "df-simulators")]: {
        value: apiResult.total_invested || 0,
        color: CHART_COLORS.INVESTED_CAPITAL,
      },
      [__("Investimento + juros líquidos acumulados", "df-simulators")]: {
        value: apiResult.net_amount| 0,
        color: CHART_COLORS.TOTAL_ACCUMULATED,
      },
    });

    // Generate chart data
    const labels = schedule.map((_, index) => {
      if (index === 0) return __("Subscrição inicial", "df-simulators");
      if (index === schedule.length - 1)
        return __("Final do prazo", "df-simulators");
      return "";
    });

    const investedData = schedule.map((item) => item.total_invested || 0);
    const accumulatedData = schedule.map((item) => item.net_total || 0);

    setChartData({
      labels,
      datasets: [
        {
          label: __("Capital investido", "df-simulators"),
          data: investedData,
          borderColor: CHART_COLORS.INVESTED_CAPITAL,
          pointBackgroundColor: CHART_COLORS.INVESTED_CAPITAL,
          pointBorderWidth: 6,
          fill: false,
          tension: 0.1,
          stepped: false,
        },
        {
          label: __("Investimento + juros líquidos acumulados", "df-simulators"),
          data: accumulatedData,
          borderColor: CHART_COLORS.TOTAL_ACCUMULATED,
          pointBackgroundColor: CHART_COLORS.TOTAL_ACCUMULATED,
          pointBorderWidth: 6,
          fill: false,
          tension: 0,
          stepped: "before",
        },
      ],
    });
  }, [response]);

  /**
   * Chart.js options with perfect tooltip and legend integration
   */
  const chartOptions = {
    layout: {
      padding: { top: 0, bottom: 0 },
    },
    borderWidth: 4,
    responsive: true,
    aspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    elements: {
      point: {
        radius: 0,
      },
    },
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip: {
        displayColors: false,
        bodyAlign: "center" as const,
        titleAlign: "center" as const,
        titleMarginBottom: 0,
        filter: (tooltipItem: any) => {
          // Only show tooltip for accumulated savings line
          return (
            tooltipItem.dataset.label ===
            __("Investimento + juros líquidos acumulados", "df-simulators")
          );
        },
        callbacks: {
          title: (tooltipItems: any[]) => {
            const dataItem = schedule[tooltipItems[0].dataIndex];
            if (dataItem.month === 0) return __("Inicial", "df-simulators");
            const adjustedMonth = dataItem.month;
            const year = Math.ceil(adjustedMonth / 12);
            const monthInYear = ((adjustedMonth - 1) % 12) + 1;

            return `Ano ${year} / Mês ${monthInYear}`;
          },
          label: (tooltipItem: any) => {
            const totalValue =
              schedule[tooltipItem.dataIndex]?.net_total || 0;
            return `Investimento: ${utils.toEuro(totalValue)}`;
          },
          footer: () => false,
          beforeLabel: (tooltipItem: any) => {
            if (Number(tooltipItem.dataIndex) !== Number(tooltipDataIndex)) {
              setTooltipDataIndex(tooltipItem.dataIndex);
              updateLegend(tooltipItem);
            }
          },
        },
      },
    },
    scales: {
      y: {
        display: false,
      },
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          display: true,
          callback: (val: unknown, index: number, ticks: any[]) => {
            if (index === 0) return __("Subscrição inicial", "df-simulators");
            if (index === ticks.length - 1)
              return __("Final do prazo", "df-simulators");
            return "";
          },
          font: {
            family: "'Proxima Nova', sans-serif",
            size: 12,
            weight: "600",
          },
          color: "#5F5F7C",
          rotation: 0,
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          align: "center" as const,
        },
      },
    },
  };

  if (!chartData) return <p>{__("Erro ao gerar gráfico.", "df-simulators")}</p>;

  return (
    <div className="tab-savings-evolution">
      <div className="chart-line">
        <Line
          ref={chartRef}
          data={chartData}
          options={chartOptions}
          plugins={[hoverLinePlugin]}
        />
      </div>
      {legends && (
        <div className="mt-4 pt-4">
          <ChartLegend data={legends} horizontal showAll />
        </div>
      )}
    </div>
  );
};

export default TabSavingsEvolution;
